// Download — turns a server `download` push_event into a browser download
// request to a session-authenticated, tokenized download URL.
//
// LiveView can't stream a large file body over the socket, so the server
// mints a short-lived export token, then pushes `{url}`. Streaming exports use
// the browser's native download handling; document exports can opt into a
// fetch/blob strategy to avoid browsers ignoring async anchor clicks.
export default {
  mounted() {
    this.handleEvent("download", ({ url, filename, strategy }) => {
      if (typeof url !== "string" || url.length === 0) return

      const nativeDownload = () => {
        try {
          const link = document.createElement("a")
          link.href = url
          link.download = ""
          link.rel = "noopener"
          link.target = "_blank"
          link.style.display = "none"
          document.body.appendChild(link)
          link.click()
          setTimeout(() => link.remove(), 1_000)
          return
        } catch (_error) {
          // Fall through to the iframe strategy below.
        }

        const frame = document.createElement("iframe")
        frame.src = url
        frame.style.display = "none"
        frame.setAttribute("aria-hidden", "true")
        document.body.appendChild(frame)
        setTimeout(() => frame.remove(), 60_000)
      }

      const blobDownload = async () => {
        const response = await fetch(url, { credentials: "same-origin" })
        if (!response.ok) throw new Error(`download failed: ${response.status}`)

        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)
        const disposition = response.headers.get("content-disposition") || ""
        const safeName = filenameFromDisposition(disposition) || safeFilename(filename)
        const link = document.createElement("a")

        link.href = objectUrl
        if (safeName) link.download = safeName
        link.style.display = "none"
        document.body.appendChild(link)
        link.click()
        link.remove()

        setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000)
      }

      if (strategy === "fetch") {
        blobDownload().catch(nativeDownload)
      } else {
        nativeDownload()
      }
    })
  },
}

function safeFilename(value) {
  if (typeof value !== "string") return ""
  return value.replace(/[\r\n"\\]/g, "").trim()
}

function filenameFromDisposition(value) {
  if (typeof value !== "string" || value.length === 0) return ""

  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i)
  if (encoded && encoded[1]) {
    try {
      return safeFilename(decodeURIComponent(encoded[1]))
    } catch (_error) {
      return safeFilename(encoded[1])
    }
  }

  const quoted = value.match(/filename="([^"]+)"/i)
  if (quoted && quoted[1]) return safeFilename(quoted[1])

  const plain = value.match(/filename=([^;]+)/i)
  if (plain && plain[1]) return safeFilename(plain[1])

  return ""
}
